<template>
  <div class="landing" v-if="quiz">
    <div class="landing-inner">
      <button class="back-btn" @click="goBack">← Back to Directory</button>

      <div class="landing-hero">
        <div class="landing-badge">{{ quiz.category_name }}</div>
        <h1>{{ quiz.name }}</h1>
        <p class="sub-title">{{ quiz.sub_title }}</p>
      </div>

      <div class="info-cards">
        <div class="info-card">
          <div class="info-icon">⏱</div>
          <div>
            <div class="info-label">Duration</div>
            <div class="info-value">{{ quiz.duration_mins }} minutes</div>
          </div>
        </div>
        <div class="info-card">
          <div class="info-icon">📝</div>
          <div>
            <div class="info-label">Questions</div>
            <div class="info-value">{{ quiz.question_count || '—' }} items</div>
          </div>
        </div>
        <div class="info-card">
          <div class="info-icon">📊</div>
          <div>
            <div class="info-label">Report</div>
            <div class="info-value">Multi-dimensional analysis</div>
          </div>
        </div>
        <div class="info-card">
          <div class="info-icon">👁</div>
          <div>
            <div class="info-label">Total attempts</div>
            <div class="info-value">{{ quiz.views.toLocaleString() }}</div>
          </div>
        </div>
      </div>

      <div class="background-section">
        <h2>Background & References</h2>
        <p>{{ quiz.background_desc }}</p>
        <p class="independence-note">
          SignalRoom assessments are independently created for educational self-discovery. Referenced names or frameworks do not imply sponsorship, certification, or endorsement.
        </p>
      </div>

      <div class="notice-section">
        <h2>Before You Begin</h2>
        <ul>
          <li>Complete the test alone in a quiet environment for valid results.</li>
          <li>There are no right or wrong answers — choose what best reflects you.</li>
          <li>Do not use external resources or assistance during the test.</li>
          <li>Your personalized report will be generated from structured scoring.</li>
          <li>Results are for self-discovery only — not a clinical diagnosis.</li>
        </ul>
      </div>

      <button class="start-btn" @click="startTest">
        Start Assessment →
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  props: {
    quiz: { type: Object, default: null }
  },
  methods: {
    goBack () {
      if (this.$route.name === 'Home') return
      this.$router.push({ name: 'Home' })
    },
    startTest () {
      if (!this.quiz) return
      this.$router.push({ name: 'QuizTest', params: { id: String(this.quiz.id) } })
    }
  }
}
</script>

<style scoped>
.landing {
  background: linear-gradient(180deg, #f0f4fa 0%, #fff 100%);
  min-height: 80vh;
  padding: 2rem 0 4rem;
}

.landing-inner {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.back-btn {
  background: none;
  border: none;
  color: #6b7fa0;
  font-size: 0.875rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0;
}

.back-btn:hover { color: #1a3a6b; }

.landing-badge {
  display: inline-block;
  background: #1a3a6b;
  color: #e8c547;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.85rem;
  border-radius: 20px;
  margin-bottom: 1rem;
  letter-spacing: 0.05em;
}

.landing-hero h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  color: #0d1f3c;
  line-height: 1.25;
  margin-bottom: 0.5rem;
}

.sub-title {
  font-size: 1rem;
  color: #6b7fa0;
  font-style: italic;
  margin-bottom: 2rem;
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 2.5rem;
}

.info-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  gap: 0.85rem;
  align-items: center;
}

.info-icon { font-size: 1.5rem; }

.info-label {
  font-size: 0.72rem;
  color: #9aaac0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.info-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0d1f3c;
}

.background-section,
.notice-section {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 12px rgba(13,31,60,0.05);
}

.background-section h2,
.notice-section h2 {
  font-family: 'Playfair Display', serif;
  font-size: 1.15rem;
  color: #0d1f3c;
  margin-bottom: 1rem;
}

.background-section p {
  font-family: 'Source Serif 4', serif;
  font-size: 0.95rem;
  color: #4a6080;
  line-height: 1.85;
  text-align: justify;
}

.background-section .independence-note {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e8edf5;
  color: #6b7fa0;
  font-size: 0.84rem;
  text-align: left;
}

.notice-section ul {
  list-style: none;
  padding: 0;
}

.notice-section li {
  font-size: 0.9rem;
  color: #4a6080;
  padding: 0.5rem 0 0.5rem 1.5rem;
  position: relative;
  line-height: 1.6;
}

.notice-section li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #c9a227;
  font-weight: 700;
}

.start-btn {
  width: 100%;
  background: linear-gradient(135deg, #1a3a6b, #0d1f3c);
  color: #fff;
  border: none;
  padding: 1.1rem;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
}

.start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(26,58,107,0.3);
}
</style>
